/**
 * PATCH: Enhanced Qibla Compass — Stable & Production-Ready
 *
 * Replaces: frontend/src/pages/QiblaCompass.js
 *
 * Enhancements:
 *   ✓ Sensor fusion with low-pass filtering
 *   ✓ Calibration quality detection
 *   ✓ Jitter suppression
 *   ✓ Better heading normalization
 *   ✓ Magnetic declination support (optional)
 *   ✓ Calibration UI with figure-8 instructions
 *   ✓ Production-grade error handling
 */

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Compass, RotateCcw, ChevronLeft, AlertCircle, MapPin, Maximize2, X,
} from 'lucide-react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { GlassCard, SectionHeader, PageLoading } from '../components/shared';
import { prayerTimesService } from '../services/prayerTimesService';
import { geolocationService, GeolocationError } from '../services/geolocationService';
import CalibrationUI from '../components/compass/CalibrationUI';
import {
  lowPassFilterHeading,
  CalibrationDetector,
  bearingDelta,
  normalizeBearing,
  detectHeadingJump,
} from '../utils/sensorFiltering';
import { toast } from 'sonner';
import { Capacitor } from '@capacitor/core';
import {
  NativeCompass,
  isNativeCompassPlatform,
  nativeAccuracyLabel,
  nativeAccuracyRequiresCalibration,
} from '../plugins/nativeCompass';

const KAABA = { lat: 21.4225, lng: 39.8262 };
const MAGNETIC_DECLINATION = 0; // Set to your region's declination if needed

// ─── Utility functions (same as before) ─────────────────────────────────────

const toRad = (d) => (d * Math.PI) / 180;
const toDeg = (r) => (r * 180) / Math.PI;

function bearingTo(lat1, lng1, lat2, lng2) {
  const φ1 = toRad(lat1);
  const φ2 = toRad(lat2);
  const Δλ = toRad(lng2 - lng1);
  const y = Math.sin(Δλ) * Math.cos(φ2);
  const x = Math.cos(φ1) * Math.sin(φ2) - Math.sin(φ1) * Math.cos(φ2) * Math.cos(Δλ);
  return (toDeg(Math.atan2(y, x)) + 360) % 360;
}

function distanceKm(lat1, lng1, lat2, lng2) {
  const R = 6371;
  const φ1 = toRad(lat1);
  const φ2 = toRad(lat2);
  const Δφ = toRad(lat2 - lat1);
  const Δλ = toRad(lng2 - lng1);
  const a = Math.sin(Δφ / 2) ** 2 + Math.cos(φ1) * Math.cos(φ2) * Math.sin(Δλ / 2) ** 2;
  return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
}

async function lightHaptic() {
  try {
    if (!Capacitor.isNativePlatform()) return;
    const { Haptics, ImpactStyle } = await import('@capacitor/haptics');
    await Haptics.impact({ style: ImpactStyle.Light });
  } catch { /* haptics not available */ }
}

function alignmentTier(delta) {
  if (delta == null) return { tier: 'unknown', color: 'text-primary', glow: '' };
  const abs = Math.abs(delta);
  if (abs < 5)  return { tier: 'on',     color: 'text-emerald-400', glow: 'shadow-[0_0_40px_rgba(52,211,153,0.6)]' };
  if (abs < 20) return { tier: 'near',   color: 'text-amber-400',   glow: 'shadow-[0_0_28px_rgba(251,191,36,0.45)]' };
  return        { tier: 'far',           color: 'text-rose-400',    glow: 'shadow-[0_0_24px_rgba(251,113,133,0.35)]' };
}

// ─── Compass Dial Component ──────────────────────────────────────────────────

const CompassDial = ({
  size = 288,
  heading,
  hasCompass,
  qiblaBearing,
  deltaToQibla,
  embedInfo,
  calibrationStatus,
}) => {
  const discRotation = hasCompass && heading != null ? -heading : 0;
  const qiblaOnDisc = qiblaBearing != null ? qiblaBearing : 0;
  const tier = alignmentTier(deltaToQibla);
  const r = size / 2;

  return (
    <div
      className="relative mx-auto"
      style={{ width: `${size}px`, height: `${size}px` }}
      data-testid="qibla-dial"
    >
      {/* Calibration status indicator */}
      {calibrationStatus?.status === 'stuck' && (
        <div className="absolute -top-6 left-1/2 -translate-x-1/2 w-3 h-3 rounded-full bg-red-500 animate-pulse" />
      )}

      {/* Outer glow ring */}
      <div
        className={`absolute inset-0 rounded-full transition-shadow duration-500 ${tier.glow}`}
        style={{
          background: 'radial-gradient(circle at 50% 50%, rgba(212,175,55,0.10), transparent 70%)',
        }}
      />

      {/* Dial — rotating, glassmorphism */}
      <div
        className="absolute inset-2 rounded-full border border-primary/30
                   bg-surface-elevated/40 backdrop-blur-sm
                   transition-transform duration-300 ease-out"
        style={{ transform: `rotate(${discRotation}deg)` }}
        data-testid="qibla-disc"
      >
        {/* Inner subtle ring */}
        <div className="absolute inset-3 rounded-full border border-border/60" />

        {/* Cardinal letters */}
        {[
          { dir: 'N', deg: 0,   color: 'text-rose-400 font-bold' },
          { dir: 'E', deg: 90,  color: 'text-text-secondary' },
          { dir: 'S', deg: 180, color: 'text-text-secondary' },
          { dir: 'W', deg: 270, color: 'text-text-secondary' },
        ].map(({ dir, deg, color }) => {
          const off = r - 24;
          const rad = toRad(deg - 90);
          const x = Math.cos(rad) * off;
          const y = Math.sin(rad) * off;
          return (
            <div
              key={dir}
              className={`absolute font-serif text-lg ${color}`}
              style={{
                left: '50%',
                top: '50%',
                transform: `translate(calc(-50% + ${x}px), calc(-50% + ${y}px))`,
              }}
            >
              {dir}
            </div>
          );
        })}

        {/* Tick marks every 15° */}
        {Array.from({ length: 24 }).map((_, i) => {
          const long = i % 6 === 0;
          return (
            <div
              key={i}
              className={long ? 'absolute left-1/2 top-2 w-[2px] h-4 bg-primary/60' : 'absolute left-1/2 top-2 w-px h-2 bg-text-muted/40'}
              style={{
                transform: `translateX(-50%) rotate(${i * 15}deg)`,
                transformOrigin: `50% ${r - 8}px`,
              }}
            />
          );
        })}

        {/* Qibla needle — rotation relative to disc:
             Disc already applies -heading, so needle at qiblaBearing deg relative to disc
             gives absolute screen angle = -heading + qiblaBearing = correct Qibla direction */}
        <div
          className={`absolute left-1/2 top-1/2 origin-bottom transition-[color,transform] duration-300 ease-out ${tier.color}`}
          style={{
            width: '6px',
            height: `${r - 28}px`,
            transform: `translate(-50%, -100%) rotate(${qiblaOnDisc}deg)`,
            transformOrigin: '50% 100%',
            filter: 'drop-shadow(0 0 6px currentColor)',
          }}
        >
          <div
            className="w-full h-full rounded-full"
            style={{
              background: 'linear-gradient(to top, currentColor, rgba(255,255,255,0.95))',
            }}
          />
          <div
            className="absolute -top-5 left-1/2 -translate-x-1/2 w-5 h-5 rounded-sm bg-current
                       flex items-center justify-center"
            style={{ boxShadow: '0 0 12px currentColor' }}
          >
            <span className="text-[10px] font-bold text-background select-none">🕋</span>
          </div>
        </div>
      </div>

      {/* Centre pin */}
      <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
        <div className="w-3 h-3 rounded-full bg-primary border-2 border-background shadow-lg" />
      </div>

      {/* Device pointer at top */}
      <div className="absolute left-1/2 -translate-x-1/2 -top-4 text-primary">
        <div
          className="w-0 h-0 border-l-[10px] border-r-[10px] border-b-[14px]
                     border-l-transparent border-r-transparent border-b-current"
          style={{ filter: 'drop-shadow(0 0 4px currentColor)' }}
        />
      </div>

      {/* Embedded info (immersive mode) */}
      {embedInfo && (
        <div className="absolute inset-0 flex flex-col items-center justify-center pointer-events-none"
             data-testid="qibla-embed-info">
          <div className="text-[10px] uppercase tracking-[0.3em] text-text-muted/80">{embedInfo.city}</div>
          {hasCompass && deltaToQibla != null ? (
            <>
              <div className={`font-serif text-3xl mt-1 drop-shadow ${tier.color}`}>
                {Math.round(Math.abs(deltaToQibla))}°
              </div>
              <div className="text-xs text-text-secondary">{embedInfo.status}</div>
            </>
          ) : (
            <div className="text-[10px] text-text-muted/70 mt-1">{embedInfo.status}</div>
          )}
          {embedInfo.distance && (
            <div className="text-[10px] text-text-muted mt-2 font-mono">{embedInfo.distance}</div>
          )}
        </div>
      )}
    </div>
  );
};

// ─── Main Page Component ──────────────────────────────────────────────────────

const QiblaCompass = () => {
  const [coords, setCoords] = useState(null);
  const [locLabel, setLocLabel] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [heading, setHeading] = useState(null);
  const [hasCompass, setHasCompass] = useState(null);
  const [orientationGranted, setOrientationGranted] = useState(null);
  const [immersive, setImmersive] = useState(false);
  const [calibrationStatus, setCalibrationStatus] = useState(null);
  const [calibrationDismissed, setCalibrationDismissed] = useState(false);
  const [nativeCompassState, setNativeCompassState] = useState(null);

  const headingRef = useRef(null);
  const calibrationDetectorRef = useRef(new CalibrationDetector(15, 12));
  const lastHeadingRef = useRef(null);
  const nativeCompassEnabled = isNativeCompassPlatform();

  const processHeading = useCallback((nextHeading, nativePayload = null) => {
    if (nextHeading == null || Number.isNaN(nextHeading)) {
      return;
    }

    if (lastHeadingRef.current !== null && detectHeadingJump(lastHeadingRef.current, nextHeading, 45)) {
      return;
    }
    lastHeadingRef.current = nextHeading;

    const smoothed = lowPassFilterHeading(headingRef.current, nextHeading, 0.85);
    const heuristicCalibration = calibrationDetectorRef.current.addReading(smoothed);

    headingRef.current = smoothed;
    setHeading(smoothed);
    setCalibrationStatus(heuristicCalibration);
    if (nativePayload) {
      setNativeCompassState(nativePayload);
    }
    setHasCompass(true);
  }, []);

  // 1. Load location
  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      setError(null);
      const prefs = prayerTimesService.getLocalPrefs();
      if (prefs.last_location?.lat && prefs.last_location?.lng) {
        if (!cancelled) {
          setCoords({ lat: prefs.last_location.lat, lng: prefs.last_location.lng });
          setLocLabel(prefs.last_location.label || null);
          setLoading(false);
        }
        return;
      }
      try {
        const { lat, lng } = await geolocationService.requestCurrent({ timeout: 12000 });
        if (cancelled) return;
        setCoords({ lat, lng });
        try {
          const rev = await prayerTimesService.reverseGeocode(lat, lng);
          setLocLabel([rev.city, rev.country].filter(Boolean).join(', '));
        } catch { /* ignore */ }
      } catch (e) {
        if (!cancelled) {
          setError(e instanceof GeolocationError && e.kind === 'denied'
            ? 'Géolocalisation refusée. Configurez votre ville depuis la page Horaires.'
            : (e?.message || 'Position indisponible'));
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  // 2. DeviceOrientation with enhanced filtering
  useEffect(() => {
    if (nativeCompassEnabled) {
      let listenerHandle;
      let cancelled = false;

      const handleNativeUpdate = (payload) => {
        if (cancelled) {
          return;
        }
        const preferredHeading = typeof payload?.trueHeading === 'number'
          ? payload.trueHeading
          : typeof payload?.heading === 'number'
            ? payload.heading
            : payload?.magneticHeading;
        processHeading(preferredHeading, payload);
      };

      (async () => {
        try {
          listenerHandle = await NativeCompass.addListener('compassUpdate', handleNativeUpdate);
          const started = await NativeCompass.start(
            coords ? { latitude: coords.lat, longitude: coords.lng } : {},
          );
          if (cancelled) {
            return;
          }
          setNativeCompassState(started);
          setHasCompass(Boolean(started?.available));
          if (typeof started?.heading === 'number' || typeof started?.magneticHeading === 'number') {
            handleNativeUpdate(started);
          }
        } catch (nativeError) {
          if (cancelled) {
            return;
          }
          setHasCompass(false);
          setNativeCompassState({
            permissionState: 'denied',
            accuracyLevel: 'unknown',
          });
          toast.error(nativeError?.message || 'Compas natif indisponible');
        }
      })();

      return () => {
        cancelled = true;
        if (listenerHandle) {
          listenerHandle.remove();
        }
        NativeCompass.stop().catch(() => {});
      };
    }

    if (typeof window === 'undefined' || !window.DeviceOrientationEvent) {
      setHasCompass(false);
      return undefined;
    }

    const handler = (e) => {
      let h = null;
      if (typeof e.webkitCompassHeading === 'number' && e.webkitCompassHeading >= 0) {
        // iOS Safari: already a clockwise compass heading (0 = North)
        h = e.webkitCompassHeading;
      } else if (e.absolute && typeof e.alpha === 'number') {
        // Android deviceorientationabsolute: alpha is CCW per spec → convert to CW
        h = (360 - e.alpha + 360) % 360;
      }
      // Non-absolute events are relative (not tied to North) → ignore

      if (h !== null && !Number.isNaN(h)) {
        processHeading(h);
      }
    };

    window.addEventListener('deviceorientationabsolute', handler, true);
    window.addEventListener('deviceorientation', handler, true);

    return () => {
      window.removeEventListener('deviceorientationabsolute', handler, true);
      window.removeEventListener('deviceorientation', handler, true);
    };
  }, [coords, nativeCompassEnabled, processHeading]);

  const mergedCalibrationStatus = useMemo(() => {
    if (!calibrationStatus && !nativeCompassState) {
      return null;
    }

    const nativeLevel = nativeCompassState?.accuracyLevel;
    const nativeNeedsCalibration = nativeAccuracyRequiresCalibration(nativeLevel);
    const merged = {
      ...(calibrationStatus || {}),
      nativeAccuracyLevel: nativeLevel,
      nativeAccuracy: nativeCompassState?.nativeAccuracy,
      nativeAccuracyUnit: nativeCompassState?.nativeAccuracyUnit,
      accuracyScore: nativeCompassState?.accuracyScore,
    };

    if (nativeNeedsCalibration) {
      merged.needsCalibration = true;
      merged.status = calibrationStatus?.status === 'stuck'
        ? 'stuck'
        : (nativeLevel === 'unreliable' || nativeLevel === 'uncalibrated' ? 'stuck' : 'noisy');
    }

    return merged;
  }, [calibrationStatus, nativeCompassState]);

  const requestOrientationPermission = async () => {
    try {
      if (typeof DeviceOrientationEvent !== 'undefined'
          && typeof DeviceOrientationEvent.requestPermission === 'function') {
        const res = await DeviceOrientationEvent.requestPermission();
        setOrientationGranted(res === 'granted');
        if (res !== 'granted') toast.error('Accès au compas refusé');
      } else {
        setOrientationGranted(true);
        toast.success('Compas activé');
      }
    } catch {
      toast.error("Impossible d'activer le compas");
      setOrientationGranted(false);
    }
  };

  const qiblaBearing = useMemo(
    () => (coords ? bearingTo(coords.lat, coords.lng, KAABA.lat, KAABA.lng) : null),
    [coords],
  );

  const distKm = useMemo(
    () => (coords ? distanceKm(coords.lat, coords.lng, KAABA.lat, KAABA.lng) : null),
    [coords],
  );

  const deltaToQibla = (hasCompass && heading != null && qiblaBearing != null)
    ? ((qiblaBearing - heading + 540) % 360) - 180
    : null;

  const isAligned = deltaToQibla != null && Math.abs(deltaToQibla) < 5;

  // 3. Haptic on alignment
  const wasAlignedRef = useRef(false);
  useEffect(() => {
    if (isAligned && !wasAlignedRef.current) {
      lightHaptic();
    }
    wasAlignedRef.current = isAligned;
  }, [isAligned]);

  // 4. Body scroll lock
  useEffect(() => {
    if (!immersive) return undefined;
    const original = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = original; };
  }, [immersive]);

  useEffect(() => {
    if (!mergedCalibrationStatus?.needsCalibration) {
      setCalibrationDismissed(false);
    }
  }, [mergedCalibrationStatus?.needsCalibration]);

  // 5. ESC to exit immersive
  useEffect(() => {
    if (!immersive) return undefined;
    const onKey = (e) => { if (e.key === 'Escape') setImmersive(false); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [immersive]);

  const statusLabel = (() => {
    if (!hasCompass) return 'Pas de compas';
    if (isAligned) return '✓ Aligné';
    if (deltaToQibla == null) return '—';
    return `${deltaToQibla > 0 ? 'Droite' : 'Gauche'} de ${Math.abs(deltaToQibla).toFixed(0)}°`;
  })();

  const openImmersive = useCallback(() => setImmersive(true), []);
  const closeImmersive = useCallback(() => setImmersive(false), []);

  return (
    <div className="min-h-screen py-8 px-4" data-testid="qibla-page">
      <div className="max-w-2xl mx-auto">
        <Link to="/prieres/horaires" className="inline-flex items-center text-sm text-text-muted hover:text-primary mb-4">
          <ChevronLeft className="w-4 h-4 mr-1" />Retour aux horaires
        </Link>

        <SectionHeader
          overline="Prières"
          title="Qibla"
          subtitle="Trouvez la direction de la Kaaba depuis votre position."
        />

        {loading ? (
          <PageLoading />
        ) : error ? (
          <GlassCard className="mt-6 text-center py-8" data-testid="qibla-error">
            <AlertCircle className="w-8 h-8 text-rose-400 mx-auto mb-2" />
            <p className="text-rose-200/90 text-sm">{error}</p>
            <Link to="/prieres/horaires" className="text-primary text-sm hover:underline mt-2 inline-block">
              Configurer ma localisation
            </Link>
          </GlassCard>
        ) : coords && (
          <>
            <GlassCard className="mt-6 flex items-center justify-between gap-3" data-testid="qibla-location">
              <div className="flex items-center gap-2 min-w-0">
                <MapPin className="w-4 h-4 text-primary shrink-0" />
                <div className="min-w-0">
                  <div className="text-sm font-serif text-text-primary truncate">{locLabel || 'Position actuelle'}</div>
                  <div className="text-xs text-text-muted font-mono">{coords.lat.toFixed(4)}, {coords.lng.toFixed(4)}</div>
                </div>
              </div>
              <div className="text-right shrink-0">
                <div className="text-xs text-text-muted uppercase tracking-wider">Distance</div>
                <div className="text-sm font-mono text-text-primary">{distKm.toFixed(0)} km</div>
              </div>
            </GlassCard>

            <GlassCard className="mt-4 space-y-4" data-testid="qibla-compass">
              {/* Calibration Status UI */}
              {!calibrationDismissed && (
                <CalibrationUI
                  needsCalibration={mergedCalibrationStatus?.needsCalibration}
                  status={mergedCalibrationStatus?.status}
                  variance={mergedCalibrationStatus?.variance}
                  nativeAccuracyLevel={mergedCalibrationStatus?.nativeAccuracyLevel}
                  nativeAccuracy={mergedCalibrationStatus?.nativeAccuracy}
                  nativeAccuracyUnit={mergedCalibrationStatus?.nativeAccuracyUnit}
                  accuracyScore={mergedCalibrationStatus?.accuracyScore}
                  onDismiss={() => setCalibrationDismissed(true)}
                />
              )}

              {/* Permission Button */}
              {!nativeCompassEnabled && typeof DeviceOrientationEvent !== 'undefined'
                && typeof DeviceOrientationEvent.requestPermission === 'function'
                && orientationGranted !== true && (
                  <Button
                    onClick={requestOrientationPermission}
                    className="w-full bg-primary text-background"
                    data-testid="qibla-enable-compass"
                  >
                    <RotateCcw className="w-4 h-4 mr-1" />Activer le compas
                  </Button>
                )}

              {/* Compass Dial */}
              <CompassDial
                size={288}
                heading={heading}
                hasCompass={hasCompass}
                qiblaBearing={qiblaBearing}
                deltaToQibla={deltaToQibla}
                calibrationStatus={mergedCalibrationStatus}
              />

              {/* Info */}
              <div className="mt-4 text-center" data-testid="qibla-info">
                <div className="text-xs text-text-muted uppercase tracking-wider">Direction de la Qibla</div>
                <div className="font-mono text-2xl text-text-primary">{qiblaBearing?.toFixed(1)}° depuis le Nord</div>
                {hasCompass ? (
                  <>
                    <div className={`mt-2 text-sm ${alignmentTier(deltaToQibla).color}`}>
                      {isAligned ? (
                        <>✓ Vous faites face à la Qibla</>
                      ) : deltaToQibla != null ? (
                        <>Tournez de <strong>{Math.abs(deltaToQibla).toFixed(0)}°</strong> vers la {deltaToQibla > 0 ? 'droite' : 'gauche'}</>
                      ) : null}
                    </div>
                    {nativeCompassEnabled && nativeCompassState?.accuracyLevel && (
                      <div className="mt-3 rounded-lg border border-border bg-surface/40 px-3 py-2 text-left">
                        <div className="flex items-center justify-between gap-3 text-xs text-text-muted">
                          <span>Precision native</span>
                          <span className="font-semibold text-text-primary">
                            {nativeAccuracyLabel(nativeCompassState.accuracyLevel)}
                          </span>
                        </div>
                        {typeof nativeCompassState.accuracyScore === 'number' && (
                          <>
                            <div className="mt-2 h-2 rounded-full bg-black/10 overflow-hidden">
                              <div
                                className={`h-full rounded-full transition-all duration-300 ${nativeCompassState.accuracyScore >= 80 ? 'bg-emerald-400' : nativeCompassState.accuracyScore >= 50 ? 'bg-amber-400' : 'bg-rose-400'}`}
                                style={{ width: `${Math.max(0, Math.min(100, Math.round(nativeCompassState.accuracyScore)))}%` }}
                              />
                            </div>
                            <div className="mt-2 text-[11px] text-text-muted">
                              Niveau de calibration: <strong>{Math.max(0, Math.min(100, Math.round(nativeCompassState.accuracyScore)))}%</strong>
                              {nativeCompassState.nativeAccuracy != null && (
                                <> · marge native {typeof nativeCompassState.nativeAccuracy === 'number' ? nativeCompassState.nativeAccuracy.toFixed(1) : nativeCompassState.nativeAccuracy} {nativeCompassState.nativeAccuracyUnit || ''}</>
                              )}
                            </div>
                          </>
                        )}
                      </div>
                    )}
                  </>
                ) : (
                  <div className="mt-2 text-xs text-text-muted">
                    <Compass className="w-3.5 h-3.5 inline mr-1" />
                    Pas de compas — utilisez l'angle ci-dessus avec une boussole physique.
                  </div>
                )}
              </div>

              {/* Immersive Button */}
              <Button
                onClick={openImmersive}
                variant="outline"
                className="w-full sm:hidden border-primary/30 text-primary"
                data-testid="qibla-immersive-btn"
              >
                <Maximize2 className="w-4 h-4 mr-2" />Mode immersif
              </Button>
            </GlassCard>
          </>
        )}
      </div>

      {/* Immersive Overlay */}
      {immersive && coords && (
        <div
          className="fixed inset-0 z-[60] flex flex-col items-center justify-between
                     bg-gradient-to-b from-[#040814] via-[#0a0f22] to-[#040814]
                     text-text-primary"
          style={{
            paddingTop: 'calc(env(safe-area-inset-top, 0px) + 16px)',
            paddingBottom: 'calc(env(safe-area-inset-bottom, 0px) + 16px)',
          }}
          data-testid="qibla-immersive-overlay"
          role="dialog"
        >
          <div
            className="absolute inset-0 pointer-events-none opacity-30"
            style={{
              background: 'radial-gradient(ellipse at 50% 0%, rgba(212,175,55,0.18), transparent 60%),'
                + 'radial-gradient(ellipse at 50% 100%, rgba(6,64,43,0.25), transparent 60%)',
            }}
          />

          {/* Exit + Title */}
          <div className="relative z-10 w-full flex items-center justify-between px-5">
            <button
              onClick={closeImmersive}
              className="flex items-center gap-2 rounded-full bg-surface/80 backdrop-blur
                         border border-border px-3 py-2 text-sm text-text-primary
                         hover:bg-surface-elevated transition-colors"
              data-testid="qibla-immersive-exit"
            >
              <X className="w-4 h-4" />Quitter
            </button>
            <div className="text-[10px] uppercase tracking-[0.4em] text-text-muted">Qibla</div>
            <div className="w-[88px]" />
          </div>

          {/* Compass */}
          <div className="relative z-10 flex-1 flex items-center justify-center w-full">
            <CompassDial
              size={Math.min(380, Math.floor((typeof window !== 'undefined' ? Math.min(window.innerWidth, window.innerHeight) : 380) * 0.85))}
              heading={heading}
              hasCompass={hasCompass}
              qiblaBearing={qiblaBearing}
              deltaToQibla={deltaToQibla}
              calibrationStatus={mergedCalibrationStatus}
              embedInfo={{
                city: locLabel || 'Position actuelle',
                status: statusLabel,
                distance: `${distKm.toFixed(0)} km vers la Mecque`,
              }}
            />
          </div>

          {/* Info */}
          <div className="relative z-10 w-full px-6 text-center">
            <div className="text-xs uppercase tracking-wider text-text-muted">Direction depuis le Nord</div>
            <div className="font-mono text-2xl text-text-primary">{qiblaBearing?.toFixed(1)}°</div>
            {hasCompass && !isAligned && deltaToQibla != null && (
              <div className={`mt-3 text-sm ${alignmentTier(deltaToQibla).color}`}>
                Tournez de <strong>{Math.abs(deltaToQibla).toFixed(0)}°</strong> vers la {deltaToQibla > 0 ? 'droite' : 'gauche'}
              </div>
            )}
            {!hasCompass && (
              <div className="mt-3 text-xs text-text-muted">
                Tenez l'appareil à plat — si la boussole ne réagit pas, calibrez en dessinant un 8 en l'air.
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default QiblaCompass;
